import React, { Suspense } from "react";
import { render, createPortal } from "react-dom";
import { ErrorBoundary } from "react-error-boundary";
import { persistQueryClient } from "react-query/persistQueryClient-experimental";
import { createLocalStoragePersistor } from "react-query/createLocalStoragePersistor-experimental";
import { QueryClient } from "react-query";
import { watchAddedNodes, domIsReady } from "../libraries/utils";


export class ReactRenderer {
  constructor() {
    this.init();
  }


  init() {
    document.addEventListener("DOMContentLoaded", async () => {
      const reactRoot = this.createReactRoot();
      const queryClient = this.createQueryClient();
      const queryParams = this.getQueryParams();
      const { enablehtmloptions } = queryParams || {};
      const enableHTMLOptions = enablehtmloptions?.[0];
      const ReactApp = await React.lazy(() =>
        import("../react/shared/containers/ReactApp")
      );
      const ReactModule = await React.lazy(() => import("./ReactModule"));

      await watchAddedNodes((elements) => {
        render(
          <ErrorBoundary
            fallbackRender={({ error }) => (
              <div style={{ display: "none" }}>
                <pre>{error.message}</pre>
              </div>
            )}
          >
            <Suspense fallback={null}>
              <ReactApp queryClient={queryClient} queryParams={queryParams}>
                {elements.map((el, index) => {
                  const name = el.dataset.react;
                  return (
                    <ErrorBoundary
                      key={`${name}-${index}`}
                      fallbackRender={() => {
                        return createPortal(
                          <div className={"d3-react-error"}>
                            Component was not loaded! Please try reloading the
                            page
                          </div>,
                          el
                        );
                      }}
                    >
                      <ReactModule
                        el={el}
                        name={name}
                        enableHTMLOptions={enableHTMLOptions}
                      />
                    </ErrorBoundary>
                  );
                })}
              </ReactApp>
            </Suspense>
          </ErrorBoundary>,
          reactRoot
        );
      }, "[data-react]");
    });
  }

  createReactRoot() {
    const root = document.createElement("div");
    root.id = "react-root";
    document.body.append(root);
    return root;
  }

  getQueryParams() {
    const getParametersFromURL = () => {
      const re = /(?:\?|&(?:amp;)?|#)([^=&#]+)(?:=?([^&#]*))/g,
        params = {},
        url = document.location.href,
        decode = (s) => decodeURIComponent(s.replace(/\+/g, ' '));
      let match;

      while ((match = re.exec(url))) {
        if (!params[decode(match[1])]) {
          params[decode(match[1])] = [];
        }
        params[decode(match[1])].push(decode(match[2]));
      }

      return params;
    };

    return getParametersFromURL();
  }

  createQueryClient() {
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          refetchIntervalInBackground: true,
        },
      },
    });
    //
    // const localStoragePersistor = createLocalStoragePersistor();
    //
    // persistQueryClient({
    //   queryClient,
    //   persistor: localStoragePersistor,
    // });

    return queryClient;
  }

  /**
   * Get ReactRenderer singlethon
   * @param {Window} win - the window instance
   * @returns {ReactRenderer} The ReactRenderer singlethon
   */
  static getInstance(win = window) {
    win.D3 = win.D3 || {};
    win.D3.reactRenderer = win.D3.reactRenderer || new ReactRenderer();

    return win.D3.reactRenderer;
  }
}

const reactRendererInstance = ReactRenderer.getInstance();
export default reactRendererInstance;
